import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ClipboardList, FileText, HelpCircle, Plus, X, Equal } from 'lucide-react';
// Tooltip component — renders into document.body via portal so it's never clipped
// by parent overflow-hidden. Positions above the trigger with a small arrow.
function InfoTooltip({
  text,
  iconClassName = 'text-slate-400'
}: {
  text: string;
  iconClassName?: string;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const triggerRef = useRef<HTMLSpanElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const updatePosition = () => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top: rect.top - 8,
      left: rect.left + rect.width / 2
    });
  };
  const show = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      updatePosition();
      setOpen(true);
    }, 120);
  };
  const hide = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setOpen(false);
  };
  useEffect(() => {
    if (!open) return;
    const onScroll = () => updatePosition();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [open]);
  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);
  return <>
      <span ref={triggerRef} onMouseEnter={show} onMouseLeave={hide} onFocus={show} onBlur={hide} tabIndex={0} className="inline-flex items-center cursor-help outline-none focus:ring-2 focus:ring-blue-300 rounded-full" aria-label={text} data-id="element-2">
        <HelpCircle size={14} className={iconClassName} data-id="element-3" />
      </span>
      {open && coords && createPortal(<div role="tooltip" style={{
      position: 'fixed',
      top: coords.top,
      left: coords.left,
      transform: 'translate(-50%, -100%)',
      zIndex: 9999,
      pointerEvents: 'none'
    }} className="animate-[tooltip-fade_120ms_ease-out]" data-id="element-4">
            <div className="relative max-w-xs bg-slate-800 text-white text-xs leading-snug px-2.5 py-1.5 rounded shadow-lg" data-id="element-5">
              {text}
              <span className="absolute left-1/2 -translate-x-1/2 -bottom-1 w-2 h-2 bg-slate-800 rotate-45" aria-hidden="true" data-id="element-6" />
            </div>
          </div>, document.body)}
    </>;
}
const fmtBps = (n: number) => Number(n).toFixed(3);
const fmtMoney = (n: number) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD'
}).format(n);
const fmtCurrencyInput = (val: string | number) => {
  const n = typeof val === 'string' ? parseFloat(val.replace(/[^0-9.-]/g, '')) : val;
  if (isNaN(n) || val === '' || val === null || val === undefined) return '';
  return new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2
  }).format(n);
};
const num = (val: string | number) => {
  if (val === '' || val === '-' || val === null || val === undefined) return 0;
  const raw = typeof val === 'string' ? val.replace(/,/g, '') : val;
  const parsed = typeof raw === 'string' ? parseFloat(raw) : raw;
  return isNaN(parsed) ? 0 : parsed;
};
// Costs are always treated as negative regardless of sign typed.
const negCost = (val: string | number) => -Math.abs(num(val));
// Normalize a cost input on blur: show with leading minus unless zero/empty.
const normalizeCostDisplay = (val: string | number) => {
  if (val === '' || val === null || val === undefined) return '';
  const n = num(val);
  if (n === 0) return '0';
  return `-${Math.abs(n)}`;
};
// Select-all-on-focus handler — lets the user immediately overwrite a default like "0".
const selectOnFocus = (e: React.FocusEvent<HTMLInputElement>) => e.target.select();
export function PricingDetail() {
  // Top Strip State
  const [loanAmount, setLoanAmount] = useState<string>('513,500');
  const [loanAmountFocused, setLoanAmountFocused] = useState(false);
  const [noteRate, setNoteRate] = useState<string | number>(7.125);
  const [lockPeriod, setLockPeriod] = useState('30 days');
  const [investor, setInvestor] = useState('Aggregator — JPM Correspondent');
  // Buy Side State
  const [buyPrice, setBuyPrice] = useState<string | number>(100.124);
  const [buyAdj, setBuyAdj] = useState<{
    label: string;
    value: string | number;
  }[]>([{
    label: '',
    value: 0
  }, {
    label: '',
    value: 0
  }, {
    label: '',
    value: 0
  }, {
    label: '',
    value: 0
  }]);
  const [buyCosts, setBuyCosts] = useState({
    lockExtension: 0 as string | number,
    lenderCredit: 0 as string | number,
    priceConcession: 0 as string | number
  });
  const [buyPoints, setBuyPoints] = useState({
    discount: 1 as string | number,
    origination: 0 as string | number
  });
  // Sell Side State
  // Sell price is now fully independent of buy price — manual entry only.
  const [sellPrice, setSellPrice] = useState<string | number>(100.124);
  // Each sell adjustment tracks its own label, plus a flag indicating whether
  // the user has manually edited it (which detaches it from the linked buy label).
  const [sellAdj, setSellAdj] = useState<{
    id: number;
    label: string;
    labelManuallyEdited: boolean;
    value: string | number;
  }[]>([{
    id: 1,
    label: '',
    labelManuallyEdited: false,
    value: 0.4
  }, {
    id: 2,
    label: '',
    labelManuallyEdited: false,
    value: 0.53
  }, {
    id: 3,
    label: '',
    labelManuallyEdited: false,
    value: 0
  }, {
    id: 4,
    label: '',
    labelManuallyEdited: false,
    value: 0
  }]);
  const [sellLockExtension, setSellLockExtension] = useState<string | number>(0);
  // Loan amount derived
  const loanAmtNum = num(loanAmount);
  // Buy Calculations — costs are always subtracted via negCost (always negative)
  const buyTotalAdj = buyAdj.reduce((acc, a) => acc + num(a.value), 0) + negCost(buyCosts.lockExtension) + negCost(buyCosts.lenderCredit) + negCost(buyCosts.priceConcession);
  const netBuyPrice = num(buyPrice) + buyTotalAdj;
  const loCompBps = netBuyPrice - 100 + num(buyPoints.discount) + num(buyPoints.origination);
  const loCompDollars = loCompBps / 100 * loanAmtNum;
  // Sell Calculations — costs always negative; sell-side Lender Credit mirrors buy.
  const sellTotalAdj = sellAdj.reduce((acc, a) => acc + num(a.value), 0) + negCost(sellLockExtension) + negCost(buyCosts.lenderCredit);
  const netSellPrice = num(sellPrice) + sellTotalAdj;
  const marginBps = netSellPrice - netBuyPrice;
  const marginDollars = marginBps / 100 * loanAmtNum;
  // Effective label shown on a sell adjustment row — if the user has edited it,
  // use the override; otherwise fall back to the linked buy adjustment label.
  const effectiveSellLabel = (index: number) => {
    const sell = sellAdj[index];
    if (!sell) return '';
    if (sell.labelManuallyEdited) return sell.label;
    const buy = buyAdj[index];
    return buy?.label ?? '';
  };
  // Handlers
  const handleBuyAdjChange = (index: number, field: 'label' | 'value', val: string) => {
    const newBuy = [...buyAdj];
    newBuy[index] = {
      ...newBuy[index],
      [field]: val
    };
    setBuyAdj(newBuy);
    // If editing a buy label, propagate to the same-index sell adjustment
    // unless the user has already manually edited that sell label.
    if (field === 'label') {
      setSellAdj(prev => prev.map((s, i) => i === index && !s.labelManuallyEdited ? {
        ...s,
        label: val
      } : s));
    }
  };
  const handleSellAdjChange = (id: number, field: 'label' | 'value', val: string) => {
    setSellAdj(sellAdj.map(a => {
      if (a.id !== id) return a;
      if (field === 'label') {
        return {
          ...a,
          label: val,
          labelManuallyEdited: true
        };
      }
      return {
        ...a,
        value: val
      };
    }));
  };
  const addSellAdj = () => {
    const newId = Math.max(0, ...sellAdj.map(a => a.id)) + 1;
    setSellAdj([...sellAdj, {
      id: newId,
      label: '',
      labelManuallyEdited: true,
      value: 0
    }]);
  };
  const removeSellAdj = (id: number) => {
    setSellAdj(sellAdj.filter(a => a.id !== id));
  };
  // Cost input blur — normalize to negative display ("0.5" → "-0.5").
  const onCostBlur = (setter: (v: string) => void) => (e: React.FocusEvent<HTMLInputElement>) => {
    setter(normalizeCostDisplay(e.target.value));
  };
  // Loan amount input handlers — show formatted with commas when blurred,
  // raw digits when focused for easy editing.
  const onLoanFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setLoanAmountFocused(true);
    // strip commas for raw editing
    const raw = String(loanAmount).replace(/,/g, '');
    setLoanAmount(raw);
    requestAnimationFrame(() => e.target.select());
  };
  const onLoanBlur = () => {
    setLoanAmountFocused(false);
    const n = num(loanAmount);
    setLoanAmount(n === 0 && loanAmount === '' ? '' : fmtCurrencyInput(n));
  };
  // Shared UI primitives
  const BadgeIN = () => <span className="text-[9px] font-bold bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded tracking-wide" data-id="element-7">
      IN
    </span>;
  const BadgeMirror = () => <span className="text-[9px] font-bold bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded tracking-wide" data-id="element-8">
      ↔
    </span>;
  const SectionHeader = ({
    title,
    badge
  }: {
    title: string;
    badge: string;
  }) => <div className="bg-slate-50 text-slate-500 text-[10px] font-bold tracking-wider uppercase py-1.5 px-4 flex justify-between items-center border-y border-slate-200" data-id="element-9">
      <span data-id="element-10">{title}</span>
      <span className="bg-slate-200 text-slate-500 px-1.5 py-0.5 rounded" data-id="element-11">
        {badge}
      </span>
    </div>;
  return <div className="print-card w-full max-w-[1400px] mx-auto bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden flex flex-col font-sans" data-id="element-12">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200" data-id="element-13">
        <div className="flex items-center gap-3" data-id="element-14">
          <div className="bg-blue-100 p-1.5 rounded text-blue-600" data-id="element-15">
            <ClipboardList size={18} data-id="element-16" />
          </div>
          <div data-id="element-17">
            <h1 className="text-base font-bold text-slate-800 leading-tight" data-id="element-18">
              Buy Side / Sell Side Pricing Detail
            </h1>
            <p className="text-xs text-slate-500" data-id="element-19">
              Live · 10:47 AM CT · Rate sheet 2026-05-12-am-r3
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-20">
          <button onClick={() => window.print()} className="no-print flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600 border border-slate-200 rounded-full hover:bg-slate-50 transition-colors" data-id="element-21">
            <FileText size={14} data-id="element-22" /> Export PDF
          </button>
        </div>
      </div>

      {/* Top Input Strip */}
      <div className="grid grid-cols-4 bg-slate-50 border-b border-slate-200 divide-x divide-slate-200" data-id="element-23">
        <div className="p-4" data-id="element-24">
          <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-25">
            Loan Amount <span className="text-red-500" data-id="element-26">*</span>
          </label>
          <div className="relative" data-id="element-27">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm" data-id="element-28">
              $
            </span>
            <input type="text" inputMode="decimal" value={loanAmount} onChange={e => setLoanAmount(e.target.value)} onFocus={onLoanFocus} onBlur={onLoanBlur} className="w-full pl-7 pr-3 py-1.5 border border-slate-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all tabular-nums" data-id="element-29" />
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5" data-id="element-30">
            Used in $ conversions for LO Comp and GOS
          </p>
        </div>
        <div className="p-4" data-id="element-31">
          <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-32">
            Current Note Rate <span className="text-red-500" data-id="element-33">*</span>
          </label>
          <div className="relative" data-id="element-34">
            <input type="number" step="0.125" value={noteRate} onFocus={selectOnFocus} onChange={e => setNoteRate(e.target.value)} className="w-full pl-3 pr-7 py-1.5 border border-slate-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all" data-id="element-35" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm" data-id="element-36">
              %
            </span>
          </div>
          <p className="text-[11px] text-slate-400 mt-1.5" data-id="element-37">
            Sell side mirrors this rate ↔
          </p>
        </div>
        <div className="p-4" data-id="element-38">
          <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-39">
            Lock Period
          </label>
          <select value={lockPeriod} onChange={e => setLockPeriod(e.target.value)} className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all bg-white" data-id="element-40">
            <option data-id="element-41">15 days</option>
            <option data-id="element-42">30 days</option>
            <option data-id="element-43">45 days</option>
            <option data-id="element-44">60 days</option>
          </select>
          <p className="text-[11px] text-slate-400 mt-1.5" data-id="element-45">
            Expires 06-11-2026
          </p>
        </div>
        <div className="p-4" data-id="element-46">
          <label className="block text-xs font-medium text-slate-600 mb-1.5" data-id="element-47">
            Investor / Channel
          </label>
          <select value={investor} onChange={e => setInvestor(e.target.value)} className="w-full px-3 py-1.5 border border-slate-300 rounded text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all bg-white" data-id="element-48">
            <option data-id="element-49">Aggregator — JPM Correspondent</option>
            <option data-id="element-50">Fannie Mae Direct</option>
            <option data-id="element-51">Freddie Mac Direct</option>
          </select>
          <p className="text-[11px] text-slate-400 mt-1.5" data-id="element-52">
            FNMA 30Y CONV · Class A · Pair-off OK
          </p>
        </div>
      </div>

      {/* Two-Column Grid */}
      <div className="flex flex-1 divide-x divide-slate-200 bg-white" data-id="element-53">
        {/* ================= BUY SIDE ================= */}
        <div className="flex-1 flex flex-col" data-id="element-54">
          <div className="bg-blue-50/50 py-2 px-4 flex justify-between items-center border-b border-slate-200" data-id="element-55">
            <div className="flex items-center gap-2" data-id="element-56">
              <span className="text-blue-700 font-bold text-sm uppercase tracking-wide" data-id="element-57">
                Buy Side
              </span>
              <span className="text-blue-400/80 text-xs" data-id="element-58">
                — what we pay the lender
              </span>
            </div>
            <span className="text-[10px] font-bold text-blue-600 uppercase tracking-wider" data-id="element-59">
              From Lender
            </span>
          </div>
          {/* Buy Side Price */}
          <div className="flex items-center justify-between py-2 px-4 border-b border-slate-100" data-id="element-60">
            <div className="flex items-center gap-2" data-id="element-61">
              <BadgeIN data-id="element-62" />
              <span className="text-sm text-slate-700" data-id="element-63">Buy Side Price</span>
              <InfoTooltip text="Base price from the lender before adjustments" data-id="element-64" />
            </div>
            <input type="number" step="0.001" value={buyPrice} onFocus={selectOnFocus} onChange={e => setBuyPrice(e.target.value)} className="w-28 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all" data-id="element-65" />
          </div>
          <SectionHeader title="Costs" badge="ALWAYS NEGATIVE" data-id="element-66" />
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-67">
            <div className="flex items-center gap-2" data-id="element-68">
              <BadgeIN data-id="element-69" />
              <span className="text-sm text-slate-700" data-id="element-70">Lock Extension</span>
              <InfoTooltip text="Cost to extend the rate lock. Always treated as negative." data-id="element-71" />
            </div>
            <input type="text" inputMode="decimal" value={buyCosts.lockExtension} onFocus={selectOnFocus} onChange={e => setBuyCosts({
            ...buyCosts,
            lockExtension: e.target.value
          })} onBlur={onCostBlur(v => setBuyCosts(prev => ({
            ...prev,
            lockExtension: v
          })))} className="w-24 text-right tabular-nums border border-rose-200 bg-rose-50 text-rose-900 rounded px-2 py-1 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition-all" data-id="element-72" />
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-73">
            <div className="flex items-center gap-2" data-id="element-74">
              <BadgeIN data-id="element-75" />
              <span className="text-sm text-slate-700" data-id="element-76">Lender Credit</span>
              <InfoTooltip text="Credit provided by the lender. Always treated as negative." data-id="element-77" />
            </div>
            <input type="text" inputMode="decimal" value={buyCosts.lenderCredit} onFocus={selectOnFocus} onChange={e => setBuyCosts({
            ...buyCosts,
            lenderCredit: e.target.value
          })} onBlur={onCostBlur(v => setBuyCosts(prev => ({
            ...prev,
            lenderCredit: v
          })))} className="w-24 text-right tabular-nums border border-rose-200 bg-rose-50 text-rose-900 rounded px-2 py-1 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition-all" data-id="element-78" />
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-79">
            <div className="flex items-center gap-2" data-id="element-80">
              <BadgeIN data-id="element-81" />
              <span className="text-sm text-slate-700" data-id="element-82">
                Price Concession / Special Incentive
              </span>
              <InfoTooltip text="Additional price concessions. Always treated as negative." data-id="element-83" />
            </div>
            <input type="text" inputMode="decimal" value={buyCosts.priceConcession} onFocus={selectOnFocus} onChange={e => setBuyCosts({
            ...buyCosts,
            priceConcession: e.target.value
          })} onBlur={onCostBlur(v => setBuyCosts(prev => ({
            ...prev,
            priceConcession: v
          })))} className="w-24 text-right tabular-nums border border-rose-200 bg-rose-50 text-rose-900 rounded px-2 py-1 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition-all" data-id="element-84" />
          </div>
          <SectionHeader title="Adjustments" badge="BPS" data-id="element-85" />
          {buyAdj.map((adj, i) => <div key={i} className="flex items-center py-1.5 px-4 border-b border-slate-100" data-id="element-86">
              <BadgeIN data-id="element-87" />
              <input type="text" value={adj.label} onChange={e => handleBuyAdjChange(i, 'label', e.target.value)} placeholder="Adjustment label..." className="ml-2 text-sm text-slate-600 bg-transparent border-none focus:ring-0 w-full outline-none placeholder:italic placeholder:text-slate-400" data-id="element-88" />
              <input type="number" step="0.01" value={adj.value} onFocus={selectOnFocus} onChange={e => handleBuyAdjChange(i, 'value', e.target.value)} className="w-24 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all bg-white" data-id="element-89" />
            </div>)}
          <div className="flex items-center justify-between py-2.5 px-4 border-t-2 border-slate-100" data-id="element-90">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wide" data-id="element-91">
              Total Adjustments
            </span>
            <span className="text-sm font-bold text-slate-800 tabular-nums" data-id="element-92">
              {fmtBps(buyTotalAdj)}
            </span>
          </div>
          <div className="flex items-center justify-between py-3 px-4 border-t-2 border-slate-200 bg-blue-50/30" data-id="element-93">
            <div className="flex items-center gap-2" data-id="element-94">
              <span className="text-sm font-bold text-blue-700 uppercase tracking-wide" data-id="element-95">
                Net Buy Price
              </span>
              <InfoTooltip text="Buy Side Price + Total Adjustments. The effective price we pay the lender." iconClassName="text-blue-400" data-id="element-96" />
            </div>
            <span className="text-base font-bold text-slate-800 tabular-nums" data-id="element-97">
              {fmtBps(netBuyPrice)}
            </span>
          </div>
          <SectionHeader title="Borrower-Paid Points" badge="BPS" data-id="element-98" />
          <div className="h-9 border-b border-slate-100" data-id="element-99"></div>
          {/* Spacer row to align with sell side */}
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-100">
            <div className="flex items-center gap-2" data-id="element-101">
              <BadgeIN data-id="element-102" />
              <span className="text-sm text-slate-700" data-id="element-103">Discount Points</span>
              <InfoTooltip text="Points paid by the borrower to buy down the rate. Adds to LO Compensation." data-id="element-104" />
            </div>
            <input type="number" step="0.001" value={buyPoints.discount} onFocus={selectOnFocus} onChange={e => setBuyPoints({
            ...buyPoints,
            discount: e.target.value
          })} className="w-24 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all" data-id="element-105" />
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-106">
            <div className="flex items-center gap-2" data-id="element-107">
              <BadgeIN data-id="element-108" />
              <span className="text-sm text-slate-700" data-id="element-109">Origination Points</span>
              <InfoTooltip text="Points paid by the borrower for loan origination. Adds to LO Compensation." data-id="element-110" />
            </div>
            <input type="number" step="0.001" value={buyPoints.origination} onFocus={selectOnFocus} onChange={e => setBuyPoints({
            ...buyPoints,
            origination: e.target.value
          })} className="w-24 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all" data-id="element-111" />
          </div>
          <div className="flex items-center justify-between py-3 px-4 border-t-2 border-slate-100 mt-auto" data-id="element-112">
            <div className="flex items-center gap-2" data-id="element-113">
              <span className="text-sm text-slate-700" data-id="element-114">LO Compensation</span>
              <InfoTooltip text="Net Buy Price − 100 + Discount Points + Origination Points. The loan officer's compensation in bps." data-id="element-115" />
            </div>
            <span className="text-sm font-bold text-slate-800 tabular-nums" data-id="element-116">
              {fmtBps(loCompBps)}
            </span>
          </div>
          <div className="flex items-center justify-between py-4 px-4 border-t-2 border-slate-200 bg-gradient-to-r from-blue-50/80 to-blue-50/20" data-id="element-117">
            <span className="text-sm font-bold text-slate-700 uppercase tracking-wide" data-id="element-118">
              LO Comp ($)
            </span>
            <span className="text-2xl font-bold text-blue-600 tabular-nums" data-id="element-119">
              {fmtMoney(loCompDollars)}
            </span>
          </div>
        </div>

        {/* ================= SELL SIDE ================= */}
        <div className="flex-1 flex flex-col" data-id="element-120">
          <div className="bg-emerald-50/50 py-2 px-4 flex justify-between items-center border-b border-slate-200" data-id="element-121">
            <div className="flex items-center gap-2" data-id="element-122">
              <span className="text-emerald-700 font-bold text-sm uppercase tracking-wide" data-id="element-123">
                Sell Side
              </span>
              <span className="text-emerald-500/80 text-xs" data-id="element-124">
                — what the investor pays us
              </span>
            </div>
            <span className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider" data-id="element-125">
              To Investor
            </span>
          </div>

          {/* Sell Side Price — independent of buy price */}
          <div className="flex items-center justify-between py-2 px-4 border-b border-slate-100" data-id="element-126">
            <div className="flex items-center gap-2" data-id="element-127">
              <BadgeIN data-id="element-128" />
              <span className="text-sm text-slate-700" data-id="element-129">Sell Side Price</span>
              <InfoTooltip text="Price the investor pays us. Enter manually — not linked to Buy Side Price." data-id="element-130" />
            </div>
            <input type="number" step="0.001" value={sellPrice} onFocus={selectOnFocus} onChange={e => setSellPrice(e.target.value)} className="w-28 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all" data-id="element-131" />
          </div>

          <SectionHeader title="Costs" badge="ALWAYS NEGATIVE" data-id="element-132" />

          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100" data-id="element-133">
            <div className="flex items-center gap-2" data-id="element-134">
              <BadgeIN data-id="element-135" />
              <span className="text-sm text-slate-700" data-id="element-136">Lock Extension</span>
              <InfoTooltip text="Cost to extend the lock on the sell side. Always treated as negative." data-id="element-137" />
            </div>
            <input type="text" inputMode="decimal" value={sellLockExtension} onFocus={selectOnFocus} onChange={e => setSellLockExtension(e.target.value)} onBlur={onCostBlur(v => setSellLockExtension(v))} className="w-24 text-right tabular-nums border border-rose-200 bg-rose-50 text-rose-900 rounded px-2 py-1 text-sm focus:border-rose-500 focus:ring-1 focus:ring-rose-500 outline-none transition-all" data-id="element-138" />
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100 bg-slate-50/50" data-id="element-139">
            <div className="flex items-center gap-2" data-id="element-140">
              <BadgeMirror data-id="element-141" />
              <span className="text-sm text-slate-600" data-id="element-142">Lender Credit</span>
              <InfoTooltip text="Mirrored from Buy Side Lender Credit — kept in sync automatically." data-id="element-143" />
            </div>
            <div className="relative w-24" data-id="element-144">
              <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-145" />
              <input readOnly value={num(buyCosts.lenderCredit) === 0 ? '0' : fmtBps(negCost(buyCosts.lenderCredit))} className="w-full text-right tabular-nums border border-transparent bg-slate-200/50 rounded px-2 py-1 text-sm text-slate-600 pl-6 outline-none" data-id="element-146" />
            </div>
          </div>

          <SectionHeader title="Adjustments" badge="BPS" data-id="element-147" />

          {sellAdj.map((adj, i) => {
          const isLinked = i < buyAdj.length;
          const displayLabel = isLinked ? effectiveSellLabel(i) : adj.label;
          return <div key={adj.id} className="flex items-center group py-1.5 px-4 border-b border-slate-100 hover:bg-slate-50 transition-colors" data-id="element-148">
                <BadgeIN data-id="element-149" />
                <input type="text" value={displayLabel} onChange={e => handleSellAdjChange(adj.id, 'label', e.target.value)} className="ml-2 text-sm text-slate-600 bg-transparent border-none focus:ring-0 w-full outline-none placeholder:italic placeholder:text-slate-400" placeholder="Adjustment label..." data-id="element-150" />
                <input type="number" step="0.01" value={adj.value} onFocus={selectOnFocus} onChange={e => handleSellAdjChange(adj.id, 'value', e.target.value)} className="w-24 text-right tabular-nums border border-slate-300 rounded px-2 py-1 text-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none transition-all bg-white" data-id="element-151" />
                <button onClick={() => removeSellAdj(adj.id)} className="ml-2 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-500 transition-all" title="Remove adjustment" data-id="element-152">
                  <X size={16} data-id="element-153" />
                </button>
              </div>;
        })}

          <div className="py-2 px-4 border-b border-slate-100 flex items-center justify-between" data-id="element-154">
            <button onClick={addSellAdj} className="flex items-center gap-1.5 text-xs font-medium text-emerald-600 border border-dashed border-emerald-300 bg-emerald-50/50 hover:bg-emerald-50 px-3 py-1.5 rounded transition-colors" data-id="element-155">
              <Plus size={14} data-id="element-156" /> Add sell-side only adjustment
            </button>
            <span className="text-[11px] text-slate-400" data-id="element-157">
              {sellAdj.length} adjustments
            </span>
          </div>

          <div className="flex items-center justify-between py-2.5 px-4 border-t-2 border-slate-100" data-id="element-158">
            <span className="text-xs font-bold text-slate-600 uppercase tracking-wide" data-id="element-159">
              Total Adjustments
            </span>
            <span className="text-sm font-bold text-slate-800 tabular-nums" data-id="element-160">
              {fmtBps(sellTotalAdj)}
            </span>
          </div>

          <div className="flex items-center justify-between py-3 px-4 border-t-2 border-slate-200 bg-emerald-50/30" data-id="element-161">
            <div className="flex items-center gap-2" data-id="element-162">
              <span className="text-sm font-bold text-emerald-700 uppercase tracking-wide" data-id="element-163">
                Net Sell Price
              </span>
              <InfoTooltip text="Sell Side Price + Total Adjustments. The effective price the investor pays us." iconClassName="text-emerald-400" data-id="element-164" />
            </div>
            <span className="text-base font-bold text-slate-800 tabular-nums" data-id="element-165">
              {fmtBps(netSellPrice)}
            </span>
          </div>

          <SectionHeader title="Sell-Side Cost Stack" badge="BPS" data-id="element-166" />

          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100 bg-slate-50/50" data-id="element-167">
            <div className="flex items-center gap-2" data-id="element-168">
              <BadgeMirror data-id="element-169" />
              <span className="text-sm text-slate-600" data-id="element-170">
                Buy Side Price Concession
              </span>
              <InfoTooltip text="Mirrored from Buy Side Price Concession / Special Incentive." data-id="element-171" />
            </div>
            <div className="relative w-24" data-id="element-172">
              <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-173" />
              <input readOnly value={num(buyCosts.priceConcession) === 0 ? '0' : fmtBps(negCost(buyCosts.priceConcession))} className="w-full text-right tabular-nums border border-transparent bg-slate-200/50 rounded px-2 py-1 text-sm text-slate-600 pl-6 outline-none" data-id="element-174" />
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100 bg-slate-50/50" data-id="element-175">
            <div className="flex items-center gap-2" data-id="element-176">
              <BadgeMirror data-id="element-177" />
              <span className="text-sm text-slate-600" data-id="element-178">Discount Points</span>
            </div>
            <div className="relative w-24" data-id="element-179">
              <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-180" />
              <input readOnly value={num(buyPoints.discount) === 0 ? '0' : fmtBps(num(buyPoints.discount))} className="w-full text-right tabular-nums border border-transparent bg-slate-200/50 rounded px-2 py-1 text-sm text-slate-600 pl-6 outline-none" data-id="element-181" />
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100 bg-slate-50/50" data-id="element-182">
            <div className="flex items-center gap-2" data-id="element-183">
              <BadgeMirror data-id="element-184" />
              <span className="text-sm text-slate-600" data-id="element-185">Origination Points</span>
            </div>
            <div className="relative w-24" data-id="element-186">
              <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-187" />
              <input readOnly value={num(buyPoints.origination) === 0 ? '0' : fmtBps(num(buyPoints.origination))} className="w-full text-right tabular-nums border border-transparent bg-slate-200/50 rounded px-2 py-1 text-sm text-slate-600 pl-6 outline-none" data-id="element-188" />
            </div>
          </div>
          <div className="flex items-center justify-between py-1.5 px-4 border-b border-slate-100 bg-slate-50/50" data-id="element-189">
            <div className="flex items-center gap-2" data-id="element-190">
              <BadgeMirror data-id="element-191" />
              <span className="text-sm text-slate-600" data-id="element-192">
                LO Compensation (cost)
              </span>
              <InfoTooltip text="Negative of Buy Side LO Compensation — the cost to us of paying the loan officer." data-id="element-193" />
            </div>
            <div className="relative w-24" data-id="element-194">
              <Equal size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" data-id="element-195" />
              <input readOnly value={loCompBps === 0 ? '0' : fmtBps(-loCompBps)} className="w-full text-right tabular-nums border border-transparent bg-slate-200/50 rounded px-2 py-1 text-sm text-slate-600 pl-6 outline-none" data-id="element-196" />
            </div>
          </div>

          <div className="flex items-center justify-between py-3 px-4 border-t-2 border-slate-100 mt-auto" data-id="element-197">
            <div className="flex items-center gap-2" data-id="element-198">
              <span className="text-sm text-slate-700" data-id="element-199">Margin / GOS</span>
              <InfoTooltip text="Net Sell Price − Net Buy Price. The gain on sale in basis points." data-id="element-200" />
            </div>
            <span className="text-sm font-bold text-slate-800 tabular-nums" data-id="element-201">
              {fmtBps(marginBps)}
            </span>
          </div>

          <div className="flex items-center justify-between py-4 px-4 border-t-2 border-slate-200 bg-gradient-to-r from-emerald-50/20 to-emerald-50/80" data-id="element-202">
            <span className="text-sm font-bold text-slate-700 uppercase tracking-wide" data-id="element-203">
              Margin / GOS ($)
            </span>
            <span className="text-2xl font-bold text-emerald-600 tabular-nums" data-id="element-204">
              {fmtMoney(marginDollars)}
            </span>
          </div>
        </div>
      </div>
    </div>;
}